import React, { useState, useCallback, useRef, useMemo } from "react";
import {
  FaAndroid,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaTimes,
  FaBullseye,
  FaExclamationTriangle,
  FaTrashAlt,
  FaEdit,
  FaDownload,
} from "react-icons/fa";
import { Drawer } from "../shared";
import { TextInput } from "../atoms";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { useAlert } from "../../context/AlertContext";
import {
  useGetApkVersionsQuery,
  useGetPresignedUploadUrlMutation,
  useConfirmApkUploadMutation,
  useDeleteApkVersionMutation,
  useUpdateApkVersionMutation,
  useLazyGetApkDownloadUrlQuery,
} from "../../store/storageRoles/apkApi";

// ─── Sabitler ───
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

const DEVICE_MODELS = [
  { id: "Cayset", label: "Cayset", color: "blue" },
  { id: "Fincanda", label: "Fincanda", color: "amber" },
  { id: "Cayset-Fincanda", label: "Cayset-Fincanda", color: "purple" },
];

// ─── SHA-256 hash hesapla (browser crypto API) ───
async function computeSha256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ─── Dosya boyutu formatter ───
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

// ─── Tarih formatter ───
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Model Multi-Select Chips ───
function ModelChipSelect({ selected, onChange }) {
  const toggle = (modelId) => {
    if (selected.includes(modelId)) {
      onChange(selected.filter((id) => id !== modelId));
    } else {
      onChange([...selected, modelId]);
    }
  };

  const colorMap = {
    blue: {
      active: "bg-blue-100 text-blue-700 border-blue-300 shadow-sm",
      inactive: "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50",
    },
    amber: {
      active: "bg-amber-100 text-amber-700 border-amber-300 shadow-sm",
      inactive: "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50",
    },
    purple: {
      active: "bg-purple-100 text-purple-700 border-purple-300 shadow-sm",
      inactive: "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50",
    },
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Hedef Modeller
      </label>
      <div className="flex flex-wrap gap-2">
        {DEVICE_MODELS.map((model) => {
          const isActive = selected.includes(model.id);
          const colors = colorMap[model.color] || colorMap.blue;
          return (
            <button
              key={model.id}
              type="button"
              onClick={() => toggle(model.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all duration-200 ${
                isActive ? colors.active : colors.inactive
              }`}
            >
              {isActive && <FaCheckCircle className="w-3 h-3" />}
              {model.label}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400">
        Seçim yapmazsanız tüm modellere gönderilir.
      </p>
    </div>
  );
}

// ─── Upload Form ───
function UploadForm({ onSuccess }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [dragActive, setDragActive] = useState(false);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [version, setVersion] = useState("");
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [selectedModels, setSelectedModels] = useState([]);
  const [minCurrentVersion, setMinCurrentVersion] = useState("");
  const [maxCurrentVersion, setMaxCurrentVersion] = useState("");
  const [targetSerialNos, setTargetSerialNos] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Upload state
  const [uploadPhase, setUploadPhase] = useState("idle"); // idle | presigning | uploading | confirming
  const [uploadProgress, setUploadProgress] = useState(0);

  const [getPresignedUrl] = useGetPresignedUploadUrlMutation();
  const [confirmUpload] = useConfirmApkUploadMutation();

  const uploading = uploadPhase !== "idle";

  const validateFile = useCallback((f) => {
    if (!f?.name?.endsWith(".apk")) {
      setFileError("Sadece .apk dosyaları yüklenebilir");
      return false;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFileError(`Dosya çok büyük (${formatFileSize(f.size)}). Maksimum 500 MB.`);
      return false;
    }
    setFileError("");
    return true;
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile && validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    },
    [validateFile]
  );

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected && validateFile(selected)) {
      setFile(selected);
    }
    e.target.value = "";
  };

  const resetForm = () => {
    setFile(null);
    setFileError("");
    setVersion("");
    setReleaseNotes([]);
    setNoteInput("");
    setSelectedModels([]);
    setMinCurrentVersion("");
    setMaxCurrentVersion("");
    setTargetSerialNos("");
    setShowAdvanced(false);
    setUploadPhase("idle");
    setUploadProgress(0);
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
  };

  const handleSubmit = async () => {
    if (!file) return showError("APK dosyası seçiniz");
    if (!version.trim()) return showError("Versiyon numarası zorunludur");
    if (file.size > MAX_FILE_SIZE) {
      return showError(`Dosya çok büyük (${formatFileSize(file.size)}). Maksimum 500 MB.`);
    }

    const serials = targetSerialNos
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      // ── Phase 1: Presigned URL al ──
      setUploadPhase("presigning");
      setUploadProgress(0);

      const presignResult = await getPresignedUrl({
        token: auth.accessToken,
        version: version.trim(),
        releaseNotes: releaseNotes.length > 0 ? JSON.stringify(releaseNotes) : undefined,
        targetModels: selectedModels.length > 0 ? JSON.stringify(selectedModels) : undefined,
        minCurrentVersion: minCurrentVersion.trim() || undefined,
        maxCurrentVersion: maxCurrentVersion.trim() || undefined,
        targetSerialNos: serials.length > 0 ? JSON.stringify(serials) : undefined,
      }).unwrap();

      const { presignedUrl, storageKey } = presignResult.data;

      // ── Phase 2: Dosyayi direkt cloud'a yukle (XHR ile progress) ──
      setUploadPhase("uploading");

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload başarısız: HTTP ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error("Bağlantı hatası"));
        xhr.ontimeout = () => reject(new Error("Zaman aşımı"));

        xhr.open("PUT", presignedUrl);
        xhr.setRequestHeader("Content-Type", "application/vnd.android.package-archive");
        xhr.send(file);
      });

      xhrRef.current = null;

      // ── Phase 3: SHA-256 hesapla ve confirm et ──
      setUploadPhase("confirming");

      let sha256Hash = "";
      try {
        sha256Hash = await computeSha256(file);
      } catch {
        // SHA hesaplama basarisiz olsa da devam et
      }

      await confirmUpload({
        token: auth.accessToken,
        storageKey,
        version: version.trim(),
        releaseNotes: releaseNotes.length > 0 ? JSON.stringify(releaseNotes) : undefined,
        fileSize: file.size,
        sha256Hash,
        targetModels: selectedModels.length > 0 ? JSON.stringify(selectedModels) : undefined,
        minCurrentVersion: minCurrentVersion.trim() || undefined,
        maxCurrentVersion: maxCurrentVersion.trim() || undefined,
        targetSerialNos: serials.length > 0 ? JSON.stringify(serials) : undefined,
      }).unwrap();

      showSuccess("APK başarıyla yüklendi");
      resetForm();
      onSuccess?.();
    } catch (err) {
      setUploadPhase("idle");
      setUploadProgress(0);
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }

      const status = err?.status;
      const msg = err?.data?.message || err?.message;

      if (status === 409) {
        showError(msg || "Bu versiyon zaten mevcut");
      } else if (status === "FETCH_ERROR" || !status) {
        showError("Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.");
      } else if (msg?.includes("Zaman aşımı") || msg?.includes("timeout")) {
        showError("Yükleme zaman aşımına uğradı. Lütfen tekrar deneyin.");
      } else {
        showError(msg || "APK yüklenirken hata oluştu");
      }
    }
  };

  // Upload phase label
  const phaseLabel = {
    presigning: "URL alınıyor...",
    uploading: `Yükleniyor %${uploadProgress}...`,
    confirming: "Kaydediliyor...",
  };

  return (
    <div className="border border-gray-100 rounded-2xl bg-white shadow-sm overflow-hidden">
      {/* Collapse header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center">
            <FaCloudUploadAlt className="w-4 h-4 text-emerald-600" />
          </div>
          <span className="text-sm font-bold text-emerald-800">Yeni APK Yükle</span>
        </div>
        {expanded ? (
          <FaChevronUp className="w-3.5 h-3.5 text-emerald-500" />
        ) : (
          <FaChevronDown className="w-3.5 h-3.5 text-emerald-500" />
        )}
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Drag & Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 ${
              uploading
                ? "cursor-wait opacity-60"
                : "cursor-pointer"
            } ${
              fileError
                ? "border-red-300 bg-red-50/30"
                : dragActive
                  ? "border-emerald-400 bg-emerald-50/50 scale-[1.01]"
                  : file
                    ? "border-emerald-300 bg-emerald-50/30"
                    : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FaAndroid className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                {!uploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setFileError("");
                    }}
                    className="ml-2 w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                  >
                    <FaTimes className="w-3 h-3 text-red-500" />
                  </button>
                )}
              </div>
            ) : (
              <>
                <FaCloudUploadAlt className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">
                  APK dosyasını sürükleyin veya{" "}
                  <span className="text-emerald-600 font-semibold">tıklayın</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">Maksimum 500 MB</p>
              </>
            )}
          </div>
          {fileError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-xl">
              <FaExclamationTriangle className="w-3 h-3 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}

          {/* Versiyon & Release Notes */}
          <div className="grid grid-cols-1 gap-3">
            <TextInput
              label="Versiyon"
              placeholder="Örn: V1.8.5(1)"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              required
            />
            {/* Değişiklik Notları — madde ekleme */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Değişiklik Notları</label>
              <div className="flex gap-2">
                <TextInput
                  placeholder="Güncelleme notu ekleyin..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && noteInput.trim()) {
                      e.preventDefault();
                      setReleaseNotes((prev) => [...prev, noteInput.trim()]);
                      setNoteInput("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (noteInput.trim()) {
                      setReleaseNotes((prev) => [...prev, noteInput.trim()]);
                      setNoteInput("");
                    }
                  }}
                  className="shrink-0 px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200 transition-colors"
                >
                  Ekle
                </button>
              </div>
              {releaseNotes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {releaseNotes.map((note, idx) => (
                    <span
                      key={idx}
                      className="flex items-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg"
                    >
                      <span>•</span>
                      <span>{note}</span>
                      <button
                        type="button"
                        onClick={() => setReleaseNotes((prev) => prev.filter((_, i) => i !== idx))}
                        className="ml-0.5 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center transition-colors"
                      >
                        <FaTimes className="w-2 h-2 text-gray-500 hover:text-red-500" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <button
            onClick={() => setShowAdvanced((p) => !p)}
            className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaBullseye className="w-3 h-3" />
            <span>Hedefleme Seçenekleri</span>
            {showAdvanced ? (
              <FaChevronUp className="w-2.5 h-2.5" />
            ) : (
              <FaChevronDown className="w-2.5 h-2.5" />
            )}
          </button>

          {showAdvanced && (
            <div className="space-y-4 pl-1 border-l-2 border-gray-100 ml-1.5">
              <div className="pl-3">
                <ModelChipSelect
                  selected={selectedModels}
                  onChange={setSelectedModels}
                />
              </div>
              <div className="pl-3 grid grid-cols-2 gap-3">
                <TextInput
                  label="Min Versiyon"
                  placeholder="Örn: 1.5.0(1)"
                  value={minCurrentVersion}
                  onChange={(e) => setMinCurrentVersion(e.target.value)}
                />
                <TextInput
                  label="Max Versiyon"
                  placeholder="Örn: V1.8.5(1)"
                  value={maxCurrentVersion}
                  onChange={(e) => setMaxCurrentVersion(e.target.value)}
                />
              </div>
              <div className="pl-3">
                <TextInput
                  label="Hedef Seri Numaraları"
                  placeholder="Örn: SN001, SN002"
                  value={targetSerialNos}
                  onChange={(e) => setTargetSerialNos(e.target.value)}
                />
                <p className="text-[10px] text-gray-400 mt-1">Beta test için belirli cihazları hedefleyin.</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={uploading || !file || !version.trim()}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold text-sm transition-all duration-300 shadow-sm ${
              uploading || !file || !version.trim()
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white hover:shadow-md active:scale-[0.98]"
            }`}
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{phaseLabel[uploadPhase] || "Yükleniyor..."}</span>
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="w-4 h-4" />
                <span>APK Yükle</span>
              </>
            )}
          </button>

          {/* Progress bar — sadece uploading fazinda goster */}
          {uploadPhase === "uploading" && (
            <div className="space-y-1">
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 text-right">
                {formatFileSize(Math.round((file?.size || 0) * uploadProgress / 100))} / {formatFileSize(file?.size || 0)}
              </p>
            </div>
          )}

          {/* Upload warning for large files */}
          {file && file.size > 100 * 1024 * 1024 && !uploading && (
            <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 px-3 py-2 rounded-xl">
              <FaExclamationTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                Büyük dosya ({formatFileSize(file.size)}). Yükleme internet hızınıza bağlı olarak
                birkaç dakika sürebilir. Lütfen sayfayı kapatmayın.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Version Card ───
function VersionCard({ version, isLatest, onDelete, deleting, canManage }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Duzenleme form state'leri
  const [editModels, setEditModels] = useState([]);
  const [editMinVersion, setEditMinVersion] = useState("");
  const [editMaxVersion, setEditMaxVersion] = useState("");
  const [editSerialNos, setEditSerialNos] = useState("");
  const [editNotes, setEditNotes] = useState([]);
  const [editNoteInput, setEditNoteInput] = useState("");
  const [editIsActive, setEditIsActive] = useState(false);

  const [updateApkVersion, { isLoading: updating }] =
    useUpdateApkVersionMutation();
  const [downloading, setDownloading] = useState(false);
  const [getDownloadUrl] = useLazyGetApkDownloadUrlQuery();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const result = await getDownloadUrl({
        token: auth.accessToken,
        id: version._id,
      }).unwrap();
      const { url, fileName } = result.data;
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || version.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      showError(err?.data?.message || "İndirme bağlantısı alınamadı");
    } finally {
      setDownloading(false);
    }
  };

  const hasTargets =
    version.targetModels?.length > 0 ||
    version.targetSerialNos?.length > 0 ||
    version.minCurrentVersion ||
    version.maxCurrentVersion;

  // Panel acildiginda mevcut degerleri doldur
  const handleEditOpen = () => {
    setEditModels(version.targetModels || []);
    setEditMinVersion(version.minCurrentVersion || "");
    setEditMaxVersion(version.maxCurrentVersion || "");
    setEditSerialNos((version.targetSerialNos || []).join(", "));
    setEditNotes(version.releaseNotes || []);
    setEditNoteInput("");
    setEditIsActive(version.isActive ?? true);
    setEditOpen(true);
    setConfirmOpen(false);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleEditSave = async () => {
    const serials = editSerialNos
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      await updateApkVersion({
        token: auth.accessToken,
        id: version._id,
        targetModels: JSON.stringify(editModels),
        minCurrentVersion: editMinVersion.trim(),
        maxCurrentVersion: editMaxVersion.trim(),
        targetSerialNos: JSON.stringify(serials),
        releaseNotes: JSON.stringify(editNotes),
        isActive: editIsActive,
      }).unwrap();
      showSuccess(`APK v${version.version} güncellendi`);
      setEditOpen(false);
    } catch (err) {
      showError(err?.data?.message || "Güncelleme sırasında hata oluştu");
    }
  };

  return (
    <div
      className={`border rounded-2xl transition-all duration-200 hover:shadow-md ${
        confirmOpen
          ? "border-red-200 bg-gradient-to-br from-red-50/30 to-white shadow-sm"
          : version.isActive
            ? "border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm"
            : "border-gray-100 bg-white"
      }`}
    >
      <div className="p-4">
        {/* Top row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                version.isActive ? "bg-emerald-100" : "bg-gray-100"
              }`}
            >
              <FaAndroid
                className={`w-4 h-4 ${
                  version.isActive ? "text-emerald-600" : "text-gray-400"
                }`}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-800">
                  {version.version}
                </span>
                {isLatest && (
                  <span className="text-[10px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md">
                    Son
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-400">
                {formatDate(version.createdInfo)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">
              {formatFileSize(version.fileSize)}
            </span>
            {version.isActive ? (
              <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-lg">
                <FaCheckCircle className="w-3 h-3" />
                Aktif
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[11px] font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                <FaTimesCircle className="w-3 h-3" />
                Pasif
              </span>
            )}
            {/* APK indir — drawer'ı açabilen herkese (hardware'deki kalıpla aynı) */}
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[11px] font-bold transition-colors disabled:opacity-50"
              title="APK dosyasını indir"
            >
              {downloading ? (
                <div className="w-3 h-3 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
              ) : (
                <FaDownload className="w-3 h-3" />
              )}
              <span>İndir</span>
            </button>
            {/* Düzenleme + silme — sadece devices.apkUpdate yetkisi olanlara */}
            {canManage && (
              <>
                <button
                  onClick={() => (editOpen ? handleEditClose() : handleEditOpen())}
                  disabled={deleting || updating}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 ${
                    editOpen
                      ? "bg-blue-100 text-blue-600"
                      : "bg-gray-50 hover:bg-blue-50 text-gray-400 hover:text-blue-500"
                  }`}
                  title="Hedefleme Düzenle"
                >
                  {editOpen ? (
                    <FaChevronUp className="w-3 h-3" />
                  ) : (
                    <FaChevronDown className="w-3 h-3" />
                  )}
                </button>
                <button
                  onClick={() => setConfirmOpen(true)}
                  disabled={confirmOpen || deleting || updating}
                  className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors disabled:opacity-50"
                  title="APK Sil"
                >
                  <FaTrashAlt className="w-3 h-3 text-red-500" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Release notes */}
        {version.releaseNotes?.length > 0 && (
          <div className="mb-3 px-3 py-2 bg-gray-50 rounded-xl space-y-1">
            {version.releaseNotes.map((note, idx) => (
              <p
                key={idx}
                className="text-xs text-gray-600 leading-relaxed flex items-start gap-1.5"
              >
                <span className="text-gray-400 mt-px">•</span>
                <span>{note}</span>
              </p>
            ))}
          </div>
        )}

        {/* Targeting info */}
        {hasTargets && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {version.targetModels?.map((model) => (
              <span
                key={model}
                className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md"
              >
                {model}
              </span>
            ))}
            {version.minCurrentVersion && (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md">
                Min: {version.minCurrentVersion}
              </span>
            )}
            {version.maxCurrentVersion && (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md">
                Max: {version.maxCurrentVersion}
              </span>
            )}
            {version.targetSerialNos?.length > 0 && (
              <span className="text-[10px] font-semibold bg-purple-50 text-purple-700 px-2 py-0.5 rounded-md">
                {version.targetSerialNos.length} cihaz hedefli
              </span>
            )}
          </div>
        )}

        {/* Inline silme onayi */}
        {confirmOpen && (
          <div className="mt-2 border-t border-red-100 pt-3 space-y-2">
            <div className="flex items-start gap-2 text-[11px] text-red-700 bg-red-50 px-3 py-2 rounded-xl">
              <FaExclamationTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                Bu işlem geri alınamaz. APK dosyası sunucudan ve veritabanından
                kalıcı olarak silinecektir.
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => onDelete?.(version)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Siliniyor...</span>
                  </>
                ) : (
                  <>
                    <FaTrashAlt className="w-2.5 h-2.5" />
                    <span>Sil</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Duzenleme Paneli (collapse/expand) ─── */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          editOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="border-t border-gray-100 bg-gradient-to-b from-blue-50/30 to-white p-4 space-y-4">
          {/* Baslik */}
          <div className="flex items-center gap-2">
            <FaEdit className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-bold text-blue-700">
              Hedefleme Düzenle
            </span>
          </div>

          {/* isActive toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-700">
              Durum
            </label>
            <button
              type="button"
              onClick={() => setEditIsActive((p) => !p)}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                editIsActive ? "bg-emerald-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                  editIsActive ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {/* Hedef Modeller */}
          <ModelChipSelect
            selected={editModels}
            onChange={setEditModels}
          />

          {/* Min/Max Versiyon */}
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              label="Min Versiyon"
              placeholder="Örn: 1.5.0(1)"
              value={editMinVersion}
              onChange={(e) => setEditMinVersion(e.target.value)}
            />
            <TextInput
              label="Max Versiyon"
              placeholder="Örn: V1.8.5(1)"
              value={editMaxVersion}
              onChange={(e) => setEditMaxVersion(e.target.value)}
            />
          </div>

          {/* Hedef Seri Numaralari */}
          <TextInput
            label="Hedef Seri Numaraları"
            placeholder="Örn: SN001, SN002"
            value={editSerialNos}
            onChange={(e) => setEditSerialNos(e.target.value)}
          />

          {/* Degisiklik Notlari */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Değişiklik Notları
            </label>
            <div className="flex gap-2">
              <TextInput
                placeholder="Güncelleme notu ekleyin..."
                value={editNoteInput}
                onChange={(e) => setEditNoteInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editNoteInput.trim()) {
                    e.preventDefault();
                    setEditNotes((prev) => [...prev, editNoteInput.trim()]);
                    setEditNoteInput("");
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (editNoteInput.trim()) {
                    setEditNotes((prev) => [...prev, editNoteInput.trim()]);
                    setEditNoteInput("");
                  }
                }}
                className="shrink-0 px-3 py-2 rounded-xl bg-blue-100 text-blue-700 text-xs font-semibold hover:bg-blue-200 transition-colors"
              >
                Ekle
              </button>
            </div>
            {editNotes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {editNotes.map((note, idx) => (
                  <span
                    key={idx}
                    className="flex items-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg"
                  >
                    <span>•</span>
                    <span>{note}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setEditNotes((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                      className="ml-0.5 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center transition-colors"
                    >
                      <FaTimes className="w-2 h-2 text-gray-500 hover:text-red-500" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Kaydet / Iptal */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={handleEditClose}
              disabled={updating}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              İptal
            </button>
            <button
              onClick={handleEditSave}
              disabled={updating}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {updating ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                <span>Kaydet</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Version List Skeleton ───
function VersionListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border border-gray-100 rounded-2xl p-4 animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gray-200" />
              <div>
                <div className="w-16 h-4 bg-gray-200 rounded mb-1" />
                <div className="w-24 h-3 bg-gray-100 rounded" />
              </div>
            </div>
            <div className="w-14 h-6 bg-gray-200 rounded-lg" />
          </div>
          <div className="w-full h-8 bg-gray-50 rounded-xl" />
        </div>
      ))}
    </div>
  );
}

// ─── Main Drawer ───
export default function ApkUpdateDrawer({ open, onClose }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("devices.apkUpdate");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteApkVersion] = useDeleteApkVersionMutation();

  const {
    data: versionsData,
    isLoading,
    isFetching,
  } = useGetApkVersionsQuery(auth.accessToken, { skip: !open });

  const versions = useMemo(
    () => versionsData?.data || [],
    [versionsData?.data]
  );

  const handleDelete = async (version) => {
    setDeletingId(version._id);
    try {
      await deleteApkVersion({
        token: auth.accessToken,
        id: version._id,
      }).unwrap();
      showSuccess(`APK v${version.version} başarıyla silindi`);
    } catch (err) {
      showError(err?.data?.message || "APK silinirken hata oluştu");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="APK Güncelleme Yönetimi"
      subtitle="Cihaz yazılım versiyonlarını yönetin"
      titleIcon={<FaAndroid className="w-5 h-5 text-emerald-500" />}
      width="md"
      headerGradient
    >
      <Drawer.Body className="p-4 space-y-5 bg-gray-50/30">
        {/* Upload Section — sadece devices.apkUpdate yetkisi olanlara */}
        {canManage && <UploadForm />}

        {/* Versions List */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FaFileAlt className="w-3.5 h-3.5 text-gray-400" />
            <h3 className="text-sm font-bold text-gray-700">
              Versiyon Geçmişi
            </h3>
            {versions.length > 0 && (
              <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md">
                {versions.length}
              </span>
            )}
          </div>

          {isLoading || isFetching ? (
            <VersionListSkeleton />
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <FaAndroid className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400 font-medium">
                Henüz yüklenmiş bir APK yok
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((v, idx) => (
                <VersionCard
                  key={v._id}
                  version={v}
                  isLatest={idx === 0}
                  onDelete={handleDelete}
                  deleting={deletingId === v._id}
                  canManage={canManage}
                />
              ))}
            </div>
          )}
        </div>
      </Drawer.Body>
    </Drawer>
  );
}
